import { useState } from 'react';
import { Editor, Node, Path, Transforms } from 'slate';
import { useSlate } from 'slate-react';
import { calculateClosestEdge } from '../utils';
import cloneElement from '@Utils/cloneElement';
import useSelectedElement from '@hooks/useSelectedElement';
import useEmailEditor from '@hooks/useEmailEditor';
import { blockLements, elements, innerBlocks } from '@elements';
import { useTranslation } from 'react-i18next';

const gapSize = 2;
const columnElement = elements['column'].node;
const rowElement = elements['row'].node;
const sectionElement = elements['section'].node;

const useOverlay = (element, path) => {
    const { i18n } = useTranslation();
    const { deselectElement, setSelectedElement } = useSelectedElement();
    const { setDraggedElement, iframeRef } = useEmailEditor();
    const iframWidth = iframeRef.current?.contentWindow?.innerWidth;
    const [dropPosition, setDropPosition] = useState(null);
    const editor = useSlate();
    // const path = ReactEditor.findPath(editor, element);
    const elementType = element.type;

    const setElement = (event, targetPath) => {
        const dataType = event.dataTransfer.types[0];
        const [action, dragedElement] = dataType.split('/');
        if (action === 'add-block') {
            let newElement = cloneElement(dragedElement);
            Transforms.insertNodes(editor, newElement, { at: targetPath });
        } else if (action === 'move-block') {
            const origionPath = JSON.parse(
                event.dataTransfer.getData(dataType),
            );
            const areSiblings = Path.isSibling(origionPath, targetPath);
            if (areSiblings && dropPosition === 'bottom') {
                targetPath = Path.previous(targetPath);
            }
            Transforms.moveNodes(editor, {
                at: origionPath,
                to: targetPath,
            });
        } else if (action === 'add-saved-block') {
            let newElement = JSON.parse(event.dataTransfer.getData(dataType));
            Transforms.insertNodes(editor, newElement, { at: targetPath });
        }

        if (dragedElement === 'section') setSelectedElement(targetPath);
        else {
            const selectionPoint = Editor.start(editor, targetPath);
            editor.select(selectionPoint);
        }
    };

    const innerInsertNode = (nodeType, event) => {
        let targetPath = path;
        let newElement = cloneElement(nodeType);

        const isEnd = ['right', 'bottom'].includes(dropPosition);
        const isSide = ['right', 'left'].includes(dropPosition);

        const parentElement = Node.parent(editor, path);
        const isSingleChildOfColumn =
            parentElement.type === 'column' &&
            parentElement.children.length === 1;

        editor.withoutNormalizing(() => {
            if (isSide) {
                if (isSingleChildOfColumn) {
                    targetPath = Path.parent(path);
                } else {
                    Transforms.wrapNodes(editor, columnElement, {
                        at: targetPath,
                    });
                }
            }

            if (isEnd) {
                targetPath = Path.next(targetPath);
            }

            setElement(event, targetPath);

            if (isSide) {
                Transforms.wrapNodes(editor, columnElement, { at: targetPath });
            }
        });
    };

    const rowInsertNode = (nodeType, event) => {
        let targetPath = path;
        let newElement = cloneElement(nodeType);

        const isEnd = ['right', 'bottom'].includes(dropPosition);

        const nextPath = Path.next(targetPath);

        if (isEnd) {
            targetPath = nextPath;
        }

        setElement(event, targetPath);
    };

    const columnInsertNode = (nodeType, event) => {
        let targetPath = path;
        let newElement = cloneElement(nodeType);

        const isEnd = ['right', 'bottom'].includes(event);
        const isSide = ['right', 'left'].includes(event);

        if (isEnd) {
            targetPath = Path.next(targetPath);
        }

        Transforms.insertNodes(editor, newElement, { at: targetPath });
        Transforms.wrapNodes(editor, columnElement, { at: targetPath });
    };

    const columnFillerInsertNode = nodeType => {
        let targetPath = [...path, element.children.length];
        let newElement = cloneElement(nodeType);
        Transforms.insertNodes(editor, newElement, { at: targetPath });
    };

    const containerInsertNode = (nodeType, event) => {
        let targetPath = [...path, 0];
        let newElement = cloneElement(nodeType);

        if (dropPosition === 'bottom') {
            targetPath = [...path, element.children.length];
        }

        setElement(event, targetPath);

        if (innerBlocks.includes(nodeType)) {
            Transforms.wrapNodes(editor, sectionElement, { at: targetPath });
        }
    };

    const sectionInsertNode = (nodeType, event) => {
        let targetPath = path;
        let newElement = cloneElement(nodeType);

        if (dropPosition === 'bottom') {
            targetPath = Path.next(targetPath);
        }

        setElement(event, targetPath);

        if (innerBlocks.includes(nodeType)) {
            Transforms.wrapNodes(editor, sectionElement, { at: targetPath });
        }
    };

    const placeholderInsertNode = (nodeType, event) => {
        editor.withoutNormalizing(() => {
            Transforms.removeNodes(editor, { at: path });
            setElement(event, path);
        });
    };

    const hasSibblings = Node.parent(editor, path).children.length > 1;

    const isFirst = !Path.hasPrevious(path);
    const isLast = !Editor.hasPath(editor, Path.next(path));

    const getRowAndSectionAncestors = () => {
        return Editor.nodes(editor, {
            at: path,
            match: n => ['row', 'section'].includes(n.type),
        });
    };

    const isTopOfSection = () => {
        // return false;
        if (!isFirst) return false;

        for (const [n, p] of getRowAndSectionAncestors()) {
            if (n.type === 'section' && !Path.hasPrevious(p)) return false;
            else if (n.type === 'row' && Path.hasPrevious(p)) return false;
        }
        return true;
    };

    const isBottomOfSection = () => {
        // return false;
        if (!isLast) return false;

        for (const [n, p] of getRowAndSectionAncestors()) {
            if (n.type === 'section' && !Editor.hasPath(editor, Path.next(p)))
                return false;
            else if (n.type === 'row' && Editor.hasPath(editor, Path.next(p)))
                return false;
        }
        return true;
    };

    const getMargins = path => {
        const prevHasMultipleChildren =
            Editor.previous(editor, { at: path })?.[0]?.children?.length > 1;
        const nextHasMultipleChildren =
            Editor.next(editor, { at: path })?.[0]?.children?.length > 1;
        const hasMultipleChildren =
            Node.get(editor, path)?.children?.length > 1;

        const gapEnd =
            hasMultipleChildren && nextHasMultipleChildren ? gapSize : 0;
        const gapStart =
            hasMultipleChildren && prevHasMultipleChildren ? gapSize : 0;

        return { gapStart, gapEnd };
    };

    const getInnerElementMargins = () => {
        const hasMultipleChildren = n => n.children.length > 1;
        const prevHasMultipleChildren = p =>
            Path.hasPrevious(p) &&
            Editor.hasPath(editor, [...Path.previous(p), 1]);
        const nextHasMultipleChildren = p =>
            Editor.hasPath(editor, [...Path.next(p), 1]);
        const isPrevoiusRow =
            Editor.previous(editor, { at: Path.parent(Path.parent(path)) })?.[0]
                ?.type === 'row';
        const isNextRow =
            Editor.next(editor, { at: Path.parent(Path.parent(path)) })?.[0]
                ?.type === 'row';

        const [gapEnd] = Editor.nodes(editor, {
            at: path,
            match: (n, p) =>
                n.type === 'row' &&
                hasMultipleChildren(n) &&
                nextHasMultipleChildren(p),
            mode: 'lowest',
        });

        const [gapStart] = Editor.nodes(editor, {
            at: path,
            match: (n, p) =>
                n.type === 'row' &&
                hasMultipleChildren(n) &&
                prevHasMultipleChildren(p),
            mode: 'lowest',
            universal: true,
        });

        const hasPrevious = Path.hasPrevious(path);
        const hasNext = Editor.hasPath(editor, Path.next(path));

        const innerGapEnd =
            (!hasNext && isNextRow && gapEnd?.[1]) || isBottomOfSection()
                ? gapSize
                : 0;
        const innerGapStart =
            (!hasPrevious && isPrevoiusRow && gapStart?.[1]) || isTopOfSection()
                ? gapSize
                : 0;

        return { innerGapStart, innerGapEnd };
    };

    const handleDragOver = (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const axis = ['row', 'section'].includes(elementType)
            ? 'y'
            : elementType === 'column'
              ? 'x'
              : undefined;
        const [position, dist] = calculateClosestEdge(
            e,
            i18n.dir(),
            iframWidth,
            axis,
        );
        setDropPosition(position);
    };

    const handleDrop = (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const dataType = e.dataTransfer.types[0];
        const [action, dragedElement] = dataType.split('/');

        if (elementType === 'section') {
            sectionInsertNode(dragedElement, e);
            endDrop();
            return;
        }
        if (elementType === 'placeholder') {
            placeholderInsertNode(dragedElement, e);
            endDrop();
            return;
        }
        if (elementType === 'container') {
            containerInsertNode(dragedElement, e);
            endDrop();
            return;
        }
        if (elementType === 'column') {
            columnInsertNode(dragedElement, e);
            endDrop();
            return;
        }
        if (elementType === 'row') {
            rowInsertNode(dragedElement, e);
            endDrop();
            return;
        }
        if (blockLements.includes(elementType)) {
            innerInsertNode(dragedElement, e);
            endDrop();
            return;
        }
    };

    const endDrop = () => {
        setDropPosition(null);
        // deselectElement();
        setDraggedElement(null);
    };

    const endDrag = () => setDropPosition(null);

    return {
        path,
        hasSibblings,
        handleDragOver,
        handleDrop,
        isFirst,
        isLast,
        dropPosition,
        endDrag,
        innerInsertNode,
        columnInsertNode,
        rowInsertNode,
        columnFillerInsertNode,
        setDropPosition,
        isTopOfSection,
        isBottomOfSection,
        ...getMargins(path),
        getInnerElementMargins,
    };
};

export default useOverlay;
